// frontend/src/pages/ClassManagementPage.js
import { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Table, Select, Typography, Space, Tooltip } from 'antd';
import { PlusOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { getAllClasses, createClass, updateClass, saveAllClassTeacherAssignments, saveAllAttendanceModes } from '../services/classService';
import { getTeachers } from '../services/userService';
import useResponsive from '../hooks/useResponsive'; // Import the hook
import { useAcademicYear } from '../context/AcademicYearContext'; // Import for refresh
import { useNotification } from '../context/NotificationContext';

const { Title } = Typography;
const { Option } = Select;

const ClassManagementPage = () => {
    const navigate = useNavigate();
    const { message } = useNotification();
    const { isMobile } = useResponsive(); // Use the hook
    const { currentYear } = useAcademicYear(); // Use context to re-fetch on year change
    const [classes, setClasses] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingClass, setEditingClass] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();


    useEffect(() => {
        if (currentYear) {
            fetchData();
        }
    }, [currentYear]); // Re-fetch data when the academic year changes

    const fetchData = async () => {
        setLoading(true);
        try {
            const [classData, teacherData] = await Promise.all([getAllClasses(), getTeachers()]);
            setClasses(classData.map(c => ({ ...c, key: c.id })));
            setTeachers(teacherData);
        } catch (error) {
            message.error('Failed to load data.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddClass = () => {
        setEditingClass(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEditClass = (record) => {
        setEditingClass(record);
        form.setFieldsValue({ name: record.name });
        setIsModalVisible(true);
    };

    const handleModalOk = () => {
        form.validateFields().then(async (values) => {
            try {
                if (editingClass) {
                    const updatedClass = await updateClass(editingClass.id, { name: values.name });
                    message.success('Class updated successfully!');
                    setClasses(prevClasses => prevClasses.map(c => c.id === updatedClass.id ? { ...c, name: updatedClass.name } : c));
                } else {
                    const newClass = await createClass(values.name);
                    message.success('Class created successfully!');
                    setClasses(prevClasses => [...prevClasses, { ...newClass, key: newClass.id, attendance_mode: 'daily' }]);
                }
                setIsModalVisible(false);
                setEditingClass(null);
                form.resetFields();
            } catch (error) {
                message.error(error.response?.data?.error || (editingClass ? 'Failed to update class.' : 'Failed to create class.'));
            }
        });
    };

    const handleTableChange = (classId, field, value) => {
        const newClasses = classes.map(c => {
            if (c.id === classId) {
                return { ...c, [field]: value };
            }
            return c;
        });
        setClasses(newClasses);
    };

    const handleSaveChanges = async () => {
        setSaving(true);
        try {
            const teacherAssignments = classes.map(c => ({
                class_id: c.id,
                teacher_id: c.class_teacher_id,
            }));
            const attendanceModes = classes.map(c => ({
                class_id: c.id,
                mode: c.attendance_mode,
            }));

            await Promise.all([
                saveAllClassTeacherAssignments(teacherAssignments),
                saveAllAttendanceModes(attendanceModes)
            ]);

            message.success('All changes saved successfully!');
            fetchData();
        } catch (error) {
            message.error('Failed to save changes.');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: 'Class Name',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            render: (text, record) => (
                <Link
                    to={`/students/class/${record.id}`}
                    style={{ fontWeight: 'bold', color: '#1890ff' }}
                >
                    {text}
                </Link>
            )
        },
        { title: 'Total Strength', dataIndex: 'total_strength', key: 'total_strength', align: 'center' },
        {
            title: 'Class Teacher',
            dataIndex: 'class_teacher_id',
            render: (_, record) => (
                <Select
                    value={record.class_teacher_id || null}
                    placeholder="Assign a teacher"
                    style={{ width: 200 }}
                    onChange={(teacherId) => handleTableChange(record.id, 'class_teacher_id', teacherId)}
                    allowClear
                >
                    {teachers.map(teacher => <Option key={teacher.id} value={teacher.id}>{teacher.username}</Option>)}
                </Select>
            )
        },
        {
            title: 'Attendance Mode',
            dataIndex: 'attendance_mode',
            render: (_, record) => (
                <Select
                    value={record.attendance_mode}
                    style={{ width: 150 }}
                    onChange={(mode) => handleTableChange(record.id, 'attendance_mode', mode)}
                >
                    <Option value="daily">Daily</Option>
                </Select>
            )
        },
        // actions column
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Class Name">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditClass(record)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Space
                style={{
                    width: '100%',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                    flexWrap: 'wrap',
                    gap: 12
                }}
            >
                <Title level={2} style={{ margin: 0 }}>Class Management</Title>
                <Space wrap>
                    <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={handleAddClass}
                        block={isMobile} // Use state for responsive block property
                    >
                        Add New Class
                    </Button>
                    <Button
                        type="default"
                        icon={<SaveOutlined />}
                        onClick={handleSaveChanges}
                        loading={saving}
                        block={isMobile} // Use state for responsive block property
                    >
                        Save Changes
                    </Button>
                </Space>
            </Space>
            <Table
                dataSource={classes}
                columns={columns}
                loading={loading}
                bordered
                scroll={{ x: 'max-content' }} // Enable horizontal scroll on small screens
                pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    onShowSizeChange: (current, size) => setPageSize(size),
                    onChange: (page, size) => setPageSize(size),
                    responsive: true,
                    showTotal: (total) => `Total ${total} classes`
                }}
            />
            <Modal
                okType='default'
                title={editingClass ? "Edit Class" : "Add New Class"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingClass(null);
                    form.resetFields();
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Class Name" rules={[{ required: true }]}>
                        <Input placeholder="e.g., Grade 10-A" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ClassManagementPage;